import React, { useEffect, useMemo, useState } from 'react';
import TimeSeriesChart from './TimeSeriesChart';
import {
    AREA_LINE_STYLES,
    SCORE_AREA_LABELS,
    SCORE_AREA_KEYS,
    SCORE_SERIES_COLORS,
    SeriesActionButtons,
    SeriesControls,
    getDefaultVisibleSeriesKeys,
} from './ScoreTrendSeries';

export { getDefaultVisibleSeriesKeys } from './ScoreTrendSeries';

const normalizeDate = (value) => new Date(value).getTime();

export function buildTrendTimeline(rawSeries, days, windowHours, now = new Date()) {
    const windowMs = windowHours * 60 * 60 * 1000;
    const lastSlot = new Date(Math.floor(now.getTime() / windowMs) * windowMs);
    const slotCount = Math.ceil((days * 24) / windowHours);
    const timestamps = new Set();

    for (let i = slotCount; i >= 0; i -= 1) {
        timestamps.add(lastSlot.getTime() - i * windowMs);
    }
    rawSeries.forEach((series) => {
        (series || []).forEach((point) => {
            const timestamp = normalizeDate(point.date);
            if (!Number.isNaN(timestamp)) timestamps.add(timestamp);
        });
    });

    return [...timestamps].sort((a, b) => a - b).map((timestamp) => new Date(timestamp).toISOString());
}

function pointsByTimestamp(points) {
    return new Map((points || []).map((point) => [normalizeDate(point.date), point.health_score]));
}

export function buildFleetScoreSeries(trendData, days, windowHours) {
    const fleetTrend = Array.isArray(trendData?.trend) ? trendData.trend : [];
    const returnedSubdomainTrends = Array.isArray(trendData?.subdomain_trends)
        ? trendData.subdomain_trends
        : [];
    const trendsBySubdomain = new Map(returnedSubdomainTrends.map((series) => [series.subdomain, series]));
    // Keep the legend stable across time ranges and during a staged rollout.
    // The API only emits areas that have scored windows, but operators should
    // still be able to see which areas have no observations in this range.
    const subdomainTrends = [
        ...SCORE_AREA_KEYS.map((subdomain) => trendsBySubdomain.get(subdomain) || { subdomain, trend: [] }),
        ...returnedSubdomainTrends.filter((series) => !SCORE_AREA_LABELS[series.subdomain]),
    ];
    const timeline = buildTrendTimeline([
        fleetTrend,
        ...subdomainTrends.map((series) => series.trend),
    ], days, windowHours);
    const allSeries = [
        {
            key: 'fleet',
            name: 'Fleet Score',
            color: SCORE_SERIES_COLORS[0],
            points: fleetTrend,
        },
        ...subdomainTrends.map((series, index) => ({
            key: `subdomain:${series.subdomain}`,
            name: SCORE_AREA_LABELS[series.subdomain] || series.subdomain,
            color: SCORE_SERIES_COLORS[(index + 1) % SCORE_SERIES_COLORS.length],
            ...AREA_LINE_STYLES[index % AREA_LINE_STYLES.length],
            points: series.trend,
        })),
    ];

    return allSeries.map((series) => {
        const values = pointsByTimestamp(series.points);
        const data = timeline.map((timestamp) => ({
            x: timestamp,
            y: values.has(normalizeDate(timestamp)) ? values.get(normalizeDate(timestamp)) : null,
        }));
        return {
            key: series.key,
            name: series.name,
            color: series.color,
            borderDash: series.borderDash,
            pointRadius: series.pointRadius,
            pointStyle: series.pointStyle,
            data,
            hasData: data.some((point) => point.y !== null),
        };
    });
}

function TrendChart({ series, timeRange, expanded = false, compact = false }) {
    return (
        <TimeSeriesChart
            title="Score Trend"
            series={series}
            yAxisLabel="%"
            timeRange={timeRange}
            timeZone="local"
            showLegend={false}
            showTitle={false}
            chartHeight={expanded ? 620 : compact ? 170 : 280}
        />
    );
}

function ExpandIcon() {
    return (
        <svg className="fleet-trend-expand-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3H3v5M3 3l6 6M16 3h5v5M21 3l-6 6M8 21H3v-5M3 21l6-6M16 21h5v-5M21 21l-6-6" />
        </svg>
    );
}

export default function FleetScoreTrendPanel({ trend, selectedDuration, compact = false }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [visibleKeys, setVisibleKeys] = useState(new Set());
    const days = selectedDuration === '1d' ? 1 : selectedDuration === '1mo' ? 30 : 7;
    const timeRange = selectedDuration === '1d' ? '24h' : selectedDuration === '1mo' ? '30d' : '7d';
    const series = useMemo(
        () => buildFleetScoreSeries(trend?.data, days, trend?.windowHours || 6),
        [trend?.data, trend?.windowHours, days]
    );

    useEffect(() => {
        if (!isExpanded) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsExpanded(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isExpanded]);

    useEffect(() => {
        setVisibleKeys(getDefaultVisibleSeriesKeys(series));
    }, [series]);

    const toggleSeries = (key) => {
        setVisibleKeys((previous) => {
            if (previous.has(key) && previous.size === 1) return previous;
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectAllSeries = () => setVisibleKeys(new Set(series.map((item) => item.key)));
    const unselectAllSeries = () => setVisibleKeys(new Set());

    const visibleSeries = series.map((item) => ({
        ...item,
        hidden: !visibleKeys.has(item.key),
    }));
    const hasData = series.some((item) => item.data.some((point) => point.y !== null));

    return (
        <div className={`fleet-trend-panel${compact ? ' fleet-trend-panel-compact' : ''}`}>
            <div className="fleet-trend-panel-header">
                <div>
                    <h3>Score Trend</h3>
                </div>
                <div className="fleet-trend-header-actions">
                    <SeriesActionButtons series={series} visibleKeys={visibleKeys} onSelectAll={selectAllSeries} onUnselectAll={unselectAllSeries} />
                    <button
                        type="button"
                        className="fleet-trend-expand-button"
                        onClick={() => setIsExpanded(true)}
                        disabled={!hasData}
                        aria-label="Expand score trend"
                        title="Expand score trend"
                    >
                        <ExpandIcon />
                    </button>
                </div>
            </div>
            <SeriesControls series={series} visibleKeys={visibleKeys} onToggle={toggleSeries} />
            <TrendChart series={visibleSeries} timeRange={timeRange} compact={compact} />

            {isExpanded && (
                <div className="fleet-trend-modal-overlay" role="presentation" onClick={() => setIsExpanded(false)}>
                    <div
                        className="fleet-trend-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="fleet-trend-modal-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="fleet-trend-modal-header">
                            <div>
                                <h2 id="fleet-trend-modal-title">Score Trend</h2>
                            </div>
                            <div className="fleet-trend-header-actions">
                                <SeriesActionButtons series={series} visibleKeys={visibleKeys} onSelectAll={selectAllSeries} onUnselectAll={unselectAllSeries} />
                                <button type="button" className="fleet-trend-modal-close" onClick={() => setIsExpanded(false)} aria-label="Close score trend">×</button>
                            </div>
                        </div>
                        <div className="fleet-trend-modal-body">
                            <SeriesControls series={series} visibleKeys={visibleKeys} onToggle={toggleSeries} />
                            <TrendChart series={visibleSeries} timeRange={timeRange} expanded />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
